import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { CheckboxPresentationProps } from "@insite/mobius/Checkbox/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getdiscrepancyCollection } from "@tKgpco/Services/DiscrepancyService";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import loadDiscrepancyCollection from "@tKgpco/Store/Data/DiscrepancyHistory/Handlers/loadDiscrepancyCollection";
import loadItemTrackingCollection from "@tKgpco/Store/Data/ItemTracking/Handlers/LoadItemTrackingCollection";
import React, { use } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => ({
    itemTrackingCollection: state.KGPState.ItemTrackingStore.itemTrackingCollection?.itemTrackings,
    itemTrackingLoading: state.KGPState.ItemTrackingStore.isLoading,
    language: state.context.session.language,
    itemTrackingParams: state.KGPState.ItemTrackingStore.itemTrackingParams,
    showPricing:
        state.context.showPricing && state.context?.session?.properties?.canShowPrice?.toLowerCase() === "true",
    currencySymbol: state.context.session.currency?.currencySymbol,
});

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const mapDispatchToProps = {
    loadItemTrackingCollection,
};

export interface ItemTrackingTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    checkboxHeader?: DataTableHeaderProps;
    checkboxCells?: DataTableCellProps;
    rowCheckbox?: CheckboxPresentationProps;
    headerContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    exportLink?: LinkPresentationProps;
    tableContainer?: InjectableCss;
}

export const ItemTrackingTableStyles: ItemTrackingTableStyles = {
    container: {
        css: css`
            overflow: auto;
            margin-top: 20px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    reorderButton: {
        color: "secondary",
        sizeVariant: "medium",
    },
    reorderButtonSpinner: {
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    checkboxHeader: {
        tight: true,
    },
    headerContainer: {
        gap: 8,
        css: css`
            padding: 20px 0;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            > * {
                padding-right: 10px;
            }
        `,
    },
    exportLink: {
        css: css`
            margin-left: 10px;
        `,
    },
    tableContainer: {
        css: css`
            border-radius: 10px 0;
            width: 100%;
            overflow: auto;
            position: relative;
        `,
    },
};

const styles = ItemTrackingTableStyles;

type sortByField = {
    [key: string]: string[];
};

const sortByFields: sortByField = {
    changeType: ["changeType"],
    changeEffectiveDate: ["changeEffectiveDate"],
};

const ItemTrackingTable: React.FC<Props> = ({
    itemTrackingCollection,
    itemTrackingLoading,
    language,
    loadItemTrackingCollection,
    itemTrackingParams,
    showPricing,
    currencySymbol,
}) => {
    if (itemTrackingLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    if (itemTrackingCollection?.length === 0 || !itemTrackingCollection) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography as="p" {...styles.noResultsText} data-test-selector="orderHistoryTable_noOrdersFound">
                    {translate("No Report found")}
                </Typography>
            </StyledWrapper>
        );
    }

    const changeSort = (sort: string) => {
        loadItemTrackingCollection({
            ...itemTrackingParams,
            sort: itemTrackingParams?.sort === sort ? `${sort} DESC` : sort,
        });
    };

    const headerClick = (sortField: string) => {
        const previousSort = itemTrackingParams?.sort ?? "";
        const [previousPrimaryField] = previousSort.split(",");
        const direction = previousPrimaryField === sortField ? " DESC" : "";
        const sort = sortByFields[sortField].map(field => `${field}${direction}`).join(", ");
        loadItemTrackingCollection({
            ...itemTrackingParams,
            sort,
        });
    };

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (!itemTrackingParams?.sort) {
            return sorted;
        }
        const [primaryField] = itemTrackingParams.sort.split(",");
        if (primaryField === sortField) {
            sorted = "ascending";
        } else if (primaryField === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.tableContainer}>
                {itemTrackingCollection?.length ? (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead>
                            <DataTableHeader className="col-date" data-test-selector="tableHeader_CustomerID">
                                {translate("Customer Item ID")}
                            </DataTableHeader>
                            <DataTableHeader className="col-date" data-test-selector="tableHeader_KGPID">
                                {translate("KGP ID")}
                            </DataTableHeader>
                            {showPricing && (
                                <DataTableHeader
                                    className="col-shipto"
                                    data-test-selector="tableHeader_VendorPartNumber"
                                >
                                    {translate("Customer Vendor Part #")}
                                </DataTableHeader>
                            )}
                            <DataTableHeader className="col-date" data-test-selector="tableHeader_Description">
                                {translate("Customer Description")}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_VendorNumber">
                                {translate("KGP Vendor #")}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_KGPVendorName">
                                {translate("KGP Vendor Name")}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_StockDirect">
                                {translate("Stock / Direct")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("changeType")}
                                onSortClick={() => headerClick("changeType")}
                                data-test-selector="tableHeader_ChangeType"
                            >
                                {translate("Change Type")}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_ChangeEntryDate">
                                {translate("Change Entry Date")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                sorted={sorted("changeEffectiveDate")}
                                onSortClick={() => headerClick("changeEffectiveDate")}
                                data-test-selector="tableHeader_ChangeEffectiveDate"
                            >
                                {translate("Change Effective Date")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_ChangeSendToCustomerDate"
                            >
                                {translate("Change Send to Customer Date")}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_ChangeNotes">
                                {translate("Change Notes")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_PreviousSellToCustomer"
                            >
                                {translate("Previous Sell to Customer")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_LastEffectiveDateForPreviousSell"
                            >
                                {translate("Last Effective Date for Previous Sell")}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_KGPSourceCost">
                                {translate("KGP Source Cost")}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_UnitPriceForCustomer"
                            >
                                {translate("Unit Price for Customer")}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_PricePer">
                                {translate(`Price "Per"`)}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_SellUOM">
                                {translate(`Sell UOM`)}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_Markup">
                                {translate(`Mark-up`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomerOrderMultiple"
                            >
                                {translate(`Customer Order Multiple`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomerMinimumOrderQuantity"
                            >
                                {translate(`Customer Minimum Order Quantity`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_KGPsVendorOrderMultiple"
                            >
                                {translate(`KGP's Vendor Order Multiple`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_KGPsVendorMinimumOrderQuantity"
                            >
                                {translate(`KGP's Vendor Minimum Order Quantity`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_KGPsVendorPartNumber"
                            >
                                {translate(`KGP's Vendor Part #`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_KGPsVendorItemDescription"
                            >
                                {translate(`KGP's Vendor Item Description`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomMadeProductForCustomers"
                            >
                                {translate(`Custom Made Product for Customers`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomerContractNumber"
                            >
                                {translate(`Customer Contract Number`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomerContractExpirationDate"
                            >
                                {translate(`Customer Contract Expiration Date`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomerCriticalIndicator"
                            >
                                {translate(`Customer Critical Indicator`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomerForSSIIndicator"
                            >
                                {translate(`Customer's For (SSI Indicator)`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomerProductManager"
                            >
                                {translate(`Customer Product Manager`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomerChangedFromItemId"
                            >
                                {translate(`Customer "Changed From" Item ID`)}
                            </DataTableHeader>
                            <DataTableHeader
                                className="col-shipto"
                                data-test-selector="tableHeader_CustomerReplacementItemId"
                            >
                                {translate(`Customer "Replacement" Item ID`)}
                            </DataTableHeader>
                            <DataTableHeader className="col-shipto" data-test-selector="tableHeader_CustomerLeadTime">
                                {translate(`Lead Time (Days) for Customer`)}
                            </DataTableHeader>
                        </DataTableHead>
                        <DataTableBody>
                            {itemTrackingCollection?.map(itemTracking => (
                                <DataTableRow key={itemTracking.id}>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerPartNumber}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">{itemTracking.itemId}</DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerSupplierPartNumber}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerSupplierItemDesc}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">{itemTracking.supplierId}</DataTableCell>
                                    <DataTableCell className="col-shipto">{itemTracking.supplierName}</DataTableCell>
                                    <DataTableCell className="col-shipto">{itemTracking.inventoryType}</DataTableCell>
                                    <DataTableCell className="col-shipto">{itemTracking.changeType}</DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.changeEntryDate
                                            ? getLocalizedDateTime({
                                                  dateTime: new Date(itemTracking.changeEntryDate),
                                                  language,
                                              })
                                            : ""}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.changeEffectiveDate
                                            ? getLocalizedDateTime({
                                                  dateTime: new Date(itemTracking.changeEffectiveDate),
                                                  language,
                                              })
                                            : ""}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.changeSentToCustomerDate
                                            ? getLocalizedDateTime({
                                                  dateTime: new Date(itemTracking.changeSentToCustomerDate),
                                                  language,
                                              })
                                            : ""}
                                    </DataTableCell>

                                    <DataTableCell className="col-shipto">{itemTracking.changeNote}</DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.previousSellPrice}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.lastEffectiveDatePrevSellPrice
                                            ? getLocalizedDateTime({
                                                  dateTime: new Date(itemTracking.lastEffectiveDatePrevSellPrice),
                                                  language,
                                              })
                                            : ""}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.sourceCost ? `${currencySymbol}${itemTracking.sourceCost}` : ""}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerUnitPrice
                                            ? `${currencySymbol}${itemTracking.customerUnitPrice}`
                                            : ""}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerPerPrice ? `${itemTracking.customerPerPrice}` : ""}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">{itemTracking.sellUOM}</DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.markUp ? `${itemTracking.markUp}` : ""}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerOrderMultipleText}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerMinOrderQtyText}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.supplierOrderMultipleText}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.supplierMinOrderQtyText}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.supplierPartNumber}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.supplierItemDesc}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customMadeForCustomer === true
                                            ? translate("Yes")
                                            : itemTracking.customMadeForCustomer === false
                                              ? translate("No")
                                              : ""}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">{itemTracking.contractNumber}</DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.contractExpiryDate
                                            ? getLocalizedDateTime({
                                                  dateTime: new Date(itemTracking.contractExpiryDate),
                                                  language,
                                              })
                                            : ""}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerCriticalIndicator}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerSSIIndicator}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.customerProductManager}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.changedFromCustomerItemNumber}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">
                                        {itemTracking.replacementCustomerItemNumber}
                                    </DataTableCell>
                                    <DataTableCell className="col-shipto">{itemTracking.leadTimeText}</DataTableCell>
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                ) : null}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(ItemTrackingTable)),
    definition: {
        group: "Common",
        displayName: "ItemTrackingTable",
    },
};

export default widgetModule;
